import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Trash2, Calculator, Loader2, AlertTriangle, CheckCircle2, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, apiError, eur } from "@/lib/apiClient";

const RUOLI = { aderente: "Aderente", coniuge: "Coniuge / unito civilmente", convivente: "Convivente di fatto", figlio: "Figlio", altro: "Altra persona" };
const FRAZ = { 1: "Annuale", 2: "Semestrale", 4: "Trimestrale" };
const ORDER = ["salute_single", "salute_famiglia", "sostegno_single", "sostegno_famiglia"];
const today = () => new Date().toISOString().slice(0, 10);
const itDate = (s) => (s ? s.split("-").reverse().join("/") : "");
const field = "w-full rounded-lg border border-input bg-white px-3 py-2 text-sm text-forest outline-none focus:border-forest";

function PersonRow({ p, i, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_60px_32px] items-center gap-2" data-testid={`cs-person-${i}`}>
      <input className={field} value={p.alias} onChange={(e) => onChange({ alias: e.target.value })} placeholder="Alias (es. Adulto 1)" data-testid={`cs-alias-${i}`} />
      <select className={field} value={p.ruolo} onChange={(e) => onChange({ ruolo: e.target.value })} data-testid={`cs-ruolo-${i}`}>
        {Object.entries(RUOLI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <input className={field} type="number" min={0} max={120} value={p.eta} onChange={(e) => onChange({ eta: e.target.value })} placeholder="Età" data-testid={`cs-eta-${i}`} />
      <button onClick={onRemove} className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-red-50 hover:text-red-600" data-testid={`cs-remove-${i}`} aria-label="Rimuovi"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

function Options({ res }) {
  return (
    <div className="overflow-x-auto rounded-xl border">
      <table className="w-full text-sm" data-testid="cs-options">
        <thead className="bg-sage text-left text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="px-3 py-2">Piano</th><th className="px-3 py-2">Copre tutti?</th><th className="px-3 py-2 text-right">Annuo</th><th className="px-3 py-2 text-right">Detrazione 19%</th></tr></thead>
        <tbody>
          {ORDER.map((k) => {
            const o = res.opzioni[k];
            const best = res.scelta === k;
            return (
              <tr key={k} className={`border-t ${best ? "bg-terracotta/10 font-semibold" : ""}`} data-testid={`cs-opt-${k}`}>
                <td className="px-3 py-2 text-forest">{o.nome}{best && <span className="ml-2 rounded-full bg-terracotta px-2 py-0.5 text-[10px] uppercase text-white">Proposta</span>}</td>
                <td className="px-3 py-2 text-muted-foreground">{!o.available ? (o.motivo || "Non disponibile") : o.complete ? "Sì" : `No: ${(o.esclusi || []).map((e) => e.alias).join(", ")}`}</td>
                <td className="px-3 py-2 text-right text-forest">{o.available ? eur(o.totale) : "—"}</td>
                <td className="px-3 py-2 text-right text-muted-foreground">{o.available ? eur(o.detrazione) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Result({ res }) {
  if (res.errors?.length) return <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700" data-testid="cs-errors">{res.errors.join(" ")}</div>;
  if (!res.scelta || !res.piano) {
    const motivi = ORDER.map((k) => res.opzioni[k]).flatMap((o) => (o.esclusi || []).map((e) => `${e.alias}: ${e.motivo}`).concat(o.motivo ? [o.motivo] : []));
    return (
      <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700" data-testid="cs-errors">
        Nessun piano 4You disponibile per queste persone.{[...new Set(motivi)].map((m) => <div key={m}>• {m}</div>)}
      </div>
    );
  }
  const pg = res.piano;
  return (
    <div className="space-y-5" data-testid="cs-result">
      <div className="rounded-2xl bg-forest p-6 text-white">
        <div className="text-[11px] font-bold uppercase tracking-wider text-sector-yellow">Soluzione proposta</div>
        <div className="mt-1 font-heading text-2xl font-semibold" data-testid="cs-scelta">{res.opzioni[res.scelta]?.nome}</div>
        <div className="mt-3 flex flex-wrap gap-6 text-sm">
          <span>Contributo annuo <b className="text-lg" data-testid="cs-annuo">{eur(res.annuo)}</b></span>
          <span>Detrazione 19% fino a <b data-testid="cs-detrazione">{eur(res.detrazione)}</b>/anno</span>
          <span>Effetto {itDate(pg.effetto)} · Scadenza {itDate(pg.scadenza)}</span>
        </div>
      </div>
      <Options res={res} />
      <div className="rounded-xl border bg-white p-4">
        <div className="mb-2 text-sm font-semibold text-forest">Piano dei versamenti</div>
        <div className="divide-y text-sm" data-testid="cs-schedule">
          {pg.rows.map((r, i) => (
            <div key={i} className="grid grid-cols-2 gap-x-3 gap-y-0.5 py-2 sm:grid-cols-[1.2fr_1.2fr_1.5fr_auto]"><span className="font-medium text-forest">{r.voce}</span><span className="text-muted-foreground">{r.periodo}</span><span className="text-muted-foreground">{r.quando}</span><span className="text-right font-semibold text-forest">{eur(r.importo)}</span></div>
          ))}
          <div className="flex justify-between pt-2 font-semibold text-forest"><span>Totale fino al {itDate(pg.scadenza)}</span><span data-testid="cs-totale">{eur(pg.totale)}</span></div>
        </div>
      </div>
      {res.warns?.length > 0 && (
        <div className="space-y-1 rounded-xl bg-amber-50 p-4 text-sm text-amber-900" data-testid="cs-warns">
          {res.warns.map((w, i) => <div key={i} className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{w}</div>)}
        </div>
      )}
      <p className="flex gap-2 text-xs text-muted-foreground"><CheckCircle2 className="h-4 w-4 shrink-0" />Preventivo indicativo (tariffe Ed. 01/2026). L'adesione si perfeziona con Cooperazione Salute, che comunica le date esatte delle rate.</p>
    </div>
  );
}

// Calcolo deterministico 4You: stesso motore della Skill, solo età e ruolo (nessun dato personale o sanitario).
export function CoopSaluteCalc() {
  const [persone, setPersone] = useState([{ alias: "Adulto 1", ruolo: "aderente", eta: "" }]);
  const [opts, setOpts] = useState({ sottoscrizione: today(), frazionamento: 1, pagamento: "sepa", nucleo: true });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const editId = params.get("edit");
  const [anagrafiche, setAnagrafiche] = useState([]);
  const [anagraficaId, setAnagraficaId] = useState(params.get("anagrafica") || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/anagrafiche").then(({ data }) => setAnagrafiche(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!editId) return;
    api.get(`/quotes/${editId}`).then(({ data: q }) => {
      const i = q.coopsalute_input || {};
      setAnagraficaId(q.anagrafica_id || "");
      if (i.persone) setPersone(i.persone.map((p) => ({ alias: p.alias, ruolo: p.ruolo, eta: p.eta ?? "" })));
      setOpts((o) => ({ ...o, sottoscrizione: i.sottoscrizione || o.sottoscrizione, frazionamento: i.frazionamento || 1, pagamento: i.pagamento || "sepa", nucleo: i.nucleo ?? true }));
      if (q.calc_result) setRes(q.calc_result);
    }).catch((e) => toast.error(apiError(e)));
  }, [editId]);
  const buildBody = () => ({ ...opts, frazionamento: Number(opts.frazionamento), persone: persone.map((p) => ({ alias: p.alias, ruolo: p.ruolo, eta: p.eta === "" ? null : Number(p.eta) })) });
  const save = async () => {
    if (!anagraficaId) return toast.error("Seleziona il cliente");
    setSaving(true);
    try {
      const { data } = await api.post("/coopsalute/quotes", { ...buildBody(), anagrafica_id: anagraficaId, request_id: params.get("request") || undefined, quote_id: editId || undefined });
      toast.success(editId ? "Preventivo aggiornato nel fascicolo" : `Preventivo ${data.codice_prev || ""} salvato nel fascicolo`);
      navigate(isAdmin ? `/admin/preventivi?open=${data.id}` : `/preventivi/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const upd = (i, patch) => setPersone((ps) => ps.map((p, k) => (k === i ? { ...p, ...patch } : p)));
  const add = () => setPersone((ps) => [...ps, { alias: `Persona ${ps.length + 1}`, ruolo: ps.length === 1 ? "coniuge" : "figlio", eta: "" }]);
  const calc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coopsalute/calculate", buildBody());
      setRes(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid min-w-0 gap-6 lg:grid-cols-[420px_1fr]">
      <div className="space-y-5 rounded-2xl border bg-white p-5" data-testid="cs-form">
        <label className="block text-xs text-muted-foreground">Cliente (fascicolo)
          <select className={field} value={anagraficaId} onChange={(e) => setAnagraficaId(e.target.value)} disabled={!!editId} data-testid="cs-anagrafica">
            <option value="">Seleziona il cliente…</option>
            {anagrafiche.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
          </select>
        </label>
        <div>
          <div className="mb-2 text-sm font-semibold text-forest">Persone da assicurare</div>
          <p className="mb-3 text-xs text-muted-foreground">Usa solo alias, ruolo ed età: niente nomi, codici fiscali o dati sulla salute.</p>
          <div className="space-y-2">{persone.map((p, i) => <PersonRow key={i} p={p} i={i} onChange={(x) => upd(i, x)} onRemove={() => setPersone((ps) => ps.filter((_, k) => k !== i))} />)}</div>
          <button onClick={add} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-terracotta-hover hover:underline" data-testid="cs-add-person"><Plus className="h-4 w-4" />Aggiungi persona</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-muted-foreground">Sottoscrizione<input type="date" className={field} value={opts.sottoscrizione} onChange={(e) => setOpts({ ...opts, sottoscrizione: e.target.value })} data-testid="cs-date" /></label>
          <label className="text-xs text-muted-foreground">Frazionamento<select className={field} value={opts.frazionamento} onChange={(e) => setOpts({ ...opts, frazionamento: e.target.value })} data-testid="cs-fraz">{Object.entries(FRAZ).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <label className="text-xs text-muted-foreground">Pagamento<select className={field} value={opts.pagamento} onChange={(e) => setOpts({ ...opts, pagamento: e.target.value })} data-testid="cs-pagamento"><option value="sepa">Addebito SEPA</option><option value="altro">Altro (solo annuale)</option></select></label>
          <label className="flex items-end gap-2 pb-2 text-sm text-forest"><input type="checkbox" checked={opts.nucleo} onChange={(e) => setOpts({ ...opts, nucleo: e.target.checked })} data-testid="cs-nucleo" />Unico nucleo familiare</label>
        </div>
        <button onClick={calc} disabled={busy} data-testid="cs-calc"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-terracotta py-3 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-terracotta-hover disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}Calcola preventivo
        </button>
        {res?.piano && !res.errors?.length && !(isAdmin && !editId) && (
          <button onClick={save} disabled={saving || !anagraficaId} data-testid="cs-save-quote"
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-forest py-3 text-sm font-bold uppercase tracking-wide text-forest transition-colors hover:bg-sage disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{editId ? "Aggiorna nel fascicolo" : "Salva nel fascicolo"}
          </button>
        )}
      </div>
      <div className="min-w-0">{res ? <Result res={res} /> : <div className="grid h-full min-h-[240px] place-items-center rounded-2xl border border-dashed text-sm text-muted-foreground" data-testid="cs-empty">Inserisci ruolo ed età e premi «Calcola preventivo».</div>}</div>
    </div>
  );
}
